'use client';

import { useState, useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from './useAuth';

interface UseSessionTimeoutOptions {
  timeoutMinutes?: number; // Default: 2 minutes
  warningBeforeSeconds?: number; // Default: 30 seconds before timeout
}

export function useSessionTimeout(options: UseSessionTimeoutOptions = {}) {
  const { timeoutMinutes = 2, warningBeforeSeconds = 30 } = options;
  const router = useRouter();
  const { logout, isAuthenticated } = useAuth();
  
  const [showWarning, setShowWarning] = useState(false);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [isActive, setIsActive] = useState(true);
  
  const timeoutDuration = timeoutMinutes * 60 * 1000; // Convert to milliseconds
  const warningDuration = warningBeforeSeconds * 1000;
  const warningTime = timeoutDuration - warningDuration;
  
  const lastActivityTime = useRef<number>(Date.now());
  const timeoutTimerRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimerRef = useRef<NodeJS.Timeout | null>(null);
  const countdownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const activityDebounceTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef<boolean>(false);
  const resetTimersRef = useRef<(() => void) | null>(null);
  const handleActivityRef = useRef<((event: Event) => void) | null>(null);

  // Handle logout
  const handleLogout = useCallback(async () => {
    setIsActive(false);
    setShowWarning(false);
    
    // Clear all timers
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    
    // Perform logout
    await logout();
    
    // Redirect to login
    router.push('/login');
  }, [logout, router]);

  // Reset timers on user activity - stored in ref to avoid dependency issues
  const resetTimers = useCallback(() => {
    if (!isAuthenticated) return;
    
    const now = Date.now();
    lastActivityTime.current = now;
    setShowWarning(false);
    setIsActive(true);
    
    // Clear existing timers
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
    if (activityDebounceTimerRef.current) {
      clearTimeout(activityDebounceTimerRef.current);
      activityDebounceTimerRef.current = null;
    }
    
    // Set warning timer (when to show warning)
    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      
      // Calculate actual remaining time when warning appears
      const updateRemainingTime = () => {
        const timeSinceActivity = Date.now() - lastActivityTime.current;
        const remaining = Math.max(0, Math.ceil((timeoutDuration - timeSinceActivity) / 1000));
        setTimeRemaining(remaining);
        return remaining;
      };
      
      // Set initial remaining time
      updateRemainingTime();
      
      // Start countdown interval
      countdownIntervalRef.current = setInterval(() => {
        const remaining = updateRemainingTime();
        
        if (remaining <= 0) {
          if (countdownIntervalRef.current) {
            clearInterval(countdownIntervalRef.current);
            countdownIntervalRef.current = null;
          }
        }
      }, 1000);
    }, warningTime);
    
    // Set logout timer
    timeoutTimerRef.current = setTimeout(() => {
      handleLogout();
    }, timeoutDuration);
  }, [isAuthenticated, timeoutDuration, warningTime, warningDuration, handleLogout]);

  // Store resetTimers in ref
  useEffect(() => {
    resetTimersRef.current = resetTimers;
  }, [resetTimers]);

  // Handle activity events - simple approach: only track true user interactions
  const handleActivity = useCallback((event: Event) => {
    if (!isAuthenticated) return;
    
    // Only reset on true user interactions - ignore programmatic events
    // Simple throttling: only reset if at least 5 seconds have passed since last reset
    // This prevents spam but still allows timeout to work when truly inactive
    const now = Date.now();
    const timeSinceLastReset = now - lastActivityTime.current;
    const minTimeBetweenResets = 5000; // 5 seconds minimum
    
    if (timeSinceLastReset >= minTimeBetweenResets) {
      if (resetTimersRef.current) {
        resetTimersRef.current();
      }
    } else {
      // Don't schedule a delayed reset - just ignore this activity
      // This ensures timers continue counting down when user is truly inactive
    }
  }, [isAuthenticated]);

  // Store handleActivity in ref
  useEffect(() => {
    handleActivityRef.current = handleActivity;
  }, [handleActivity]);

  // Set up activity listeners - only depend on isAuthenticated to prevent re-runs
  useEffect(() => {
    if (!isAuthenticated) {
      // Clear timers if not authenticated
      isInitializedRef.current = false;
      if (timeoutTimerRef.current) {
        clearTimeout(timeoutTimerRef.current);
        timeoutTimerRef.current = null;
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
        countdownIntervalRef.current = null;
      }
      if (activityDebounceTimerRef.current) {
        clearTimeout(activityDebounceTimerRef.current);
        activityDebounceTimerRef.current = null;
      }
      setShowWarning(false);
      return;
    }

    // Only initialize once when authenticated
    if (isInitializedRef.current) {
      return;
    }

    isInitializedRef.current = true;

    // Initialize timers on mount
    // Use a small delay to ensure refs are set up
    const initTimer = setTimeout(() => {
      if (resetTimersRef.current) {
        resetTimersRef.current();
      }
    }, 0);

    // Listen for user activity - only track true user interactions
    // Removed: 'mousemove' (fires constantly), 'scroll' (can fire programmatically), 'focus' (fires on tab focus)
    const events = ['mousedown', 'keypress', 'keydown', 'touchstart', 'click'];
    
    // Use ref to access latest handleActivity
    const activityHandler = (event: Event) => {
      if (handleActivityRef.current) {
        handleActivityRef.current(event);
      }
    };
    
    events.forEach(event => {
      window.addEventListener(event, activityHandler, { passive: true });
    });

    return () => {
      // Clear init timer
      clearTimeout(initTimer);
      
      // Cleanup event listeners
      events.forEach(event => {
        window.removeEventListener(event, activityHandler);
      });
      
      // Cleanup timers
      if (timeoutTimerRef.current) {
        clearTimeout(timeoutTimerRef.current);
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
      if (activityDebounceTimerRef.current) {
        clearTimeout(activityDebounceTimerRef.current);
      }
      isInitializedRef.current = false;
    };
  }, [isAuthenticated, timeoutMinutes, warningBeforeSeconds]);

  // Handle "Stay Logged In" from warning modal
  const handleStayLoggedIn = useCallback(() => {
    resetTimers();
  }, [resetTimers]);

  return {
    showWarning,
    timeRemaining,
    isActive,
    handleStayLoggedIn,
    handleLogout,
  };
}

